import {
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useScheduleReconciliation } from "@/hooks/useSubscriptionSchedules";
import { formatDate } from "@/utils/date-format";
import {
  BackLink,
  card,
  cell,
  Fact,
  money,
  num,
  SectionHeading,
  tableBox,
  th,
  totalRow,
} from "./shared";

const sum = (values: string[]) =>
  (values.reduce((s, v) => s + Math.round(Number(v) * 100), 0) / 100).toFixed(2);

/**
 * Why the schedules and the books disagree: payments with no schedule (the schedule backfill
 * fixes them), and 24100 entries no schedule accounts for.
 */
export default function ReconciliationDifferences({
  asOf,
  onBack,
  onOpenEntry,
}: {
  asOf: string | undefined;
  onBack: () => void;
  onOpenEntry: (journalEntryId: number) => void;
}) {
  const { data, isLoading } = useScheduleReconciliation(asOf);
  if (isLoading || !data) {
    return <Typography sx={{ color: "var(--color-graphite)" }}>Loading…</Typography>;
  }
  const differenceCents = Math.round(Number(data.difference) * 100);
  const entryLink = (id: number, no: string) => (
    <Link
      component="button"
      type="button"
      onClick={() => onOpenEntry(id)}
      sx={{ fontSize: 13, color: "var(--color-rodeo-black)" }}
    >
      {no}
    </Link>
  );

  return (
    <Box>
      <BackLink label="Deferred Subscriptions" onClick={onBack} />
      <Typography component="h1" sx={{ fontSize: 22, fontWeight: 700, mt: 1 }}>
        Reconciliation differences
      </Typography>
      <Typography sx={{ fontSize: 13, color: "var(--color-graphite)", mb: 2.5 }}>
        Why the schedules and the books disagree, as of {formatDate(data.asOf)}.
      </Typography>

      <Box
        component="section"
        aria-label="Summary"
        sx={{
          ...card,
          mb: 3.5,
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, minmax(0, 1fr))" },
          gap: 4,
        }}
      >
        <Fact label="Still owed, per schedules">{money(data.scheduleRemaining)}</Fact>
        <Fact label="Still owed, per the books">{money(data.ledgerBalance)}</Fact>
        <Fact label="Difference" tone={differenceCents === 0 ? "ok" : "warn"}>
          {money(data.difference)}
        </Fact>
        <Fact label="Explained below" tone={data.explained === data.difference ? "ok" : "warn"}>
          {money(data.explained.replace("-", ""))} of {money(data.difference.replace("-", ""))}
        </Fact>
      </Box>

      <Box component="section" aria-label="Payments with no schedule" sx={{ mb: 3.5 }}>
        <SectionHeading
          title={
            <>
              Payments with no schedule{" "}
              <Typography component="span" sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
                · {data.paymentsWithoutSchedule.length} · on the books, but nothing will release
                them to revenue
              </Typography>
            </>
          }
          note="Fixed by running the schedule backfill."
        />
        <Box sx={tableBox}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={th}>Organization</TableCell>
                <TableCell sx={th}>Invoice</TableCell>
                <TableCell sx={th}>Service term</TableCell>
                <TableCell sx={th}>Paid</TableCell>
                <TableCell sx={{ ...th, textAlign: "right" }}>Amount</TableCell>
                <TableCell sx={th}>Payment entry</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.paymentsWithoutSchedule.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ ...cell, color: "var(--color-graphite)" }}>
                    None. Every payment has its schedule.
                  </TableCell>
                </TableRow>
              )}
              {data.paymentsWithoutSchedule.map((p) => (
                <TableRow key={p.journalEntryId} hover>
                  <TableCell sx={cell}>{p.organizationName ?? "—"}</TableCell>
                  <TableCell sx={cell}>
                    {p.stripeInvoiceNumber ?? (p.renewalId ? `Renewal #${p.renewalId}` : "—")}
                  </TableCell>
                  <TableCell sx={cell}>
                    {p.cycleStart && p.cycleEnd
                      ? `${formatDate(p.cycleStart)} – ${formatDate(p.cycleEnd)}`
                      : "Renewal not found"}
                  </TableCell>
                  <TableCell sx={cell}>{formatDate(p.paidOn)}</TableCell>
                  <TableCell sx={num}>{money(p.amount)}</TableCell>
                  <TableCell sx={cell}>{entryLink(p.journalEntryId, p.journalNo)}</TableCell>
                </TableRow>
              ))}
              {data.paymentsWithoutSchedule.length > 0 && (
                <TableRow sx={totalRow}>
                  <TableCell sx={cell} colSpan={4}>
                    Total
                  </TableCell>
                  <TableCell sx={num}>
                    {money(sum(data.paymentsWithoutSchedule.map((p) => p.amount)))}
                  </TableCell>
                  <TableCell sx={cell} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Box>

      <Box component="section" aria-label="Entries no schedule explains">
        <SectionHeading
          title={
            <>
              Deferred revenue entries no schedule explains{" "}
              <Typography component="span" sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
                · {data.unexplainedEntries.length}
              </Typography>
            </>
          }
        />
        {data.unexplainedEntries.length === 0 ? (
          <Box sx={{ ...card, fontSize: 13, color: "var(--color-graphite)" }}>
            None. Every other deferred revenue entry belongs to a schedule: the payment that created
            it, or a month it recognised.
          </Box>
        ) : (
          <Box sx={tableBox}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={th}>Date</TableCell>
                  <TableCell sx={th}>Journal entry</TableCell>
                  <TableCell sx={th}>Reason</TableCell>
                  <TableCell sx={th}>Memo</TableCell>
                  <TableCell sx={{ ...th, textAlign: "right" }}>Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.unexplainedEntries.map((e) => (
                  <TableRow key={e.journalEntryId} hover>
                    <TableCell sx={cell}>{formatDate(e.accountingDate)}</TableCell>
                    <TableCell sx={cell}>{entryLink(e.journalEntryId, e.journalNo)}</TableCell>
                    <TableCell sx={cell}>{e.reasonCode}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{e.memo ?? "—"}</TableCell>
                    <TableCell sx={num}>{money(e.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
    </Box>
  );
}
